import { AxiosError } from "axios";

import { apiGupShup } from "@/lib/axios";

export class GupShupIntegrationService {
  private partnerToken: string | null = null;

  async login(email: string, password: string): Promise<string> {
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("password", password);

      const response = await apiGupShup.post("/account/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const token = response.data?.token || response.data?.accessToken;
      if (!token) {
        throw new Error("Token not found in login response");
      }

      this.partnerToken = token;
      return token;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to login to GupShup: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to login to GupShup");
    }
  }

  setPartnerToken(token: string): void {
    this.partnerToken = token;
  }

  getPartnerToken(): string | null {
    return this.partnerToken;
  }

  async findAppByName(appName: string, partnerToken: string): Promise<{ appId: string } | null> {
    try {
      const params = new URLSearchParams({
        name: appName,
        pageNo: "1",
        pageSize: "10",
      });

      const response = await apiGupShup.get(`/app/list?${params}`, {
        headers: {
          token: partnerToken,
        },
      });

      let apps: any[] = [];
      if (Array.isArray(response.data)) {
        apps = response.data;
      } else if (Array.isArray(response.data?.data)) {
        apps = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        
        const keys = Object.keys(response.data);
        for (const key of keys) {
          if (Array.isArray(response.data[key])) {
            apps = response.data[key];
            break;
          }
        }
      }

      const app = apps.find((a: { name: string }) => a.name === appName);
      
      if (app?.id) {
        return { appId: app.id };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  async createApp(appName: string, partnerToken: string): Promise<{ appId: string }> {
    try {
      const formData = new URLSearchParams();
      formData.append("name", appName);
      formData.append("templateMessaging", "true");
      formData.append("disableOptinPrefUrl", "false");

      const response = await apiGupShup.post("/app", formData, {
        headers: {
          token: partnerToken, 
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const appId = response.data?.appId || response.data?.id;
      if (!appId) {
        throw new Error("App ID not found in create app response");
      }

      return { appId };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        
        if (error.response?.status === 409) {
          const existingApp = await this.findAppByName(appName, partnerToken);
          
          if (existingApp) {
            return existingApp;
          }
          
          throw new Error(
            `App já existe mas não foi possível encontrá-lo: ${error.response?.data?.message || error.message}`
          );
        }
        
        throw new Error(
          `Failed to create app: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to create app");
    }
  }

  async setAppContact(
    appId: string,
    contactEmail: string,
    contactName: string,
    contactNumber: string,
    partnerToken: string
  ): Promise<void> {
    try {
      const formData = new URLSearchParams();
      formData.append("contactEmail", contactEmail);
      formData.append("contactName", contactName);
      formData.append("contactNumber", contactNumber);

      await apiGupShup.put(`/app/${appId}/onboarding/contact`, formData, {
        headers: {
          token: partnerToken, 
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        throw new Error(
          `Failed to set app contact: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to set app contact");
    }
  }

  async generateOnboardingLink(
    appId: string,
    userName: string,
    lang: string = "pt",
    partnerToken: string
  ): Promise<{ link: string }> {
    try {
      const params = new URLSearchParams({
        regenerate: "true",
        user: userName,
        lang: lang,
      });

      const response = await apiGupShup.get(`/app/${appId}/onboarding/embed/link?${params}`, {
        headers: {
          token: partnerToken, 
        },
      });

      const link = response.data?.link;
      if (!link) {
        throw new Error("Link not found in generate onboarding link response");
      }

      return { link };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        throw new Error(
          `Failed to generate onboarding link: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to generate onboarding link");
    }
  }

  async getAppDetails(appId: string, partnerToken: string): Promise<{
    appId: string;
    appName: string;
    status: string;
    phoneNumber?: string;
    phoneNumberId?: string;
  }> {
    try {
      const response = await apiGupShup.get(`/app/${appId}/details`, {
        headers: {
          token: partnerToken, 
        },
      });

      const appDetails = response.data?.appDetails || response.data?.data || response.data;

      const isActive = appDetails?.live === true && appDetails?.phone;

      const details = {
        appId: appDetails?.id || appId,
        appName: appDetails?.name || "",
        status: isActive ? "active" : appDetails?.status || "pending",
        phoneNumber: appDetails?.phone,
        phoneNumberId: appDetails?.phone,
      };

      return details;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        throw new Error(
          `Failed to get app details: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to get app details");
    }
  }

  async getWhatsAppProfileDisplayName(appId: string, accessToken: string): Promise<string> {
    try {
      const response = await apiGupShup.get(`/app/${appId}/profile/displayName`, {
        headers: {
          Authorization: accessToken,
        },
      });

      const displayNameInfo = response.data?.displayNameInfo;
      const verifiedName = typeof displayNameInfo?.verifiedName === "string" ? displayNameInfo.verifiedName.trim() : "";
      const requestedName = typeof displayNameInfo?.requestedName === "string" ? displayNameInfo.requestedName.trim() : "";

      // Se tiver nome verificado aprovado, preferimos; caso contrário, usamos o nome solicitado.
      return verifiedName || requestedName || "";
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        if (error.response?.status === 403) {
          throw new Error("FORBIDDEN: no permissions to access display name");
        }

        throw new Error(`Failed to get display name: ${error.response?.data?.message || error.message}`);
      }

      throw new Error("Failed to get display name");
    }
  }

  async getAppToken(appId: string, partnerToken: string): Promise<string> {
    try {
      
      let response;
      try {
        response = await apiGupShup.get(`/app/${appId}/token`, {
          headers: {
            token: partnerToken, 
          },
        });
      } catch (tokenError) {
        
        if (tokenError instanceof AxiosError && (tokenError.response?.status === 401 || tokenError.response?.status === 403)) {
          response = await apiGupShup.get(`/app/${appId}/token`, {
            headers: {
              Authorization: partnerToken,
            },
          });
        } else {
          throw tokenError;
        }
      }

      const appToken = response.data?.token?.token;
      if (!appToken) {
        throw new Error("App token not found in response");
      }

      return appToken;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        if (error.response?.status === 403) {
          throw new Error("FORBIDDEN: Não tem permissão para obter app token");
        }
        throw new Error(
          `Failed to get app token: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to get app token");
    }
  }

  async configureSubscription(
    appId: string,
    callbackUrl: string,
    modes: string[],
    tag: string,
    version: string,
    partnerToken: string
  ): Promise<{ status: string }> {
    let normalizedUrl = callbackUrl.trim().replace(/\/+$/, "");

    try {
      
      try {
        new URL(callbackUrl);
      } catch (urlError) {
        throw new Error(`Invalid callback URL: ${callbackUrl}`);
      }

      const urlObj = new URL(callbackUrl);
      if (urlObj.protocol !== "https:") {
        throw new Error("Callback URL must use HTTPS protocol");
      }
      if (urlObj.hostname === "localhost" || urlObj.hostname === "127.0.0.1") {
        throw new Error("Callback URL cannot be localhost. Must be a publicly accessible URL");
      }

      const validModesForV3 = [
        "NONE",
        "MESSAGE",
        "READ",
        "DELIVERED",
        "SENT",
        "DELETED",
        "FAILED",
        "ENQUEUED",
        "FLOWS_MESSAGE",
        "PAYMENTS",
        "TEMPLATE",
        "ACCOUNT",
        "BILLING",
        "ALL",
        "OTHERS",
        "COEXISTENCE",
      ];
      const filteredModes = modes.filter(mode => validModesForV3.includes(mode));
      
      if (filteredModes.length === 0) {
        filteredModes.push("ALL");
      }

      const formData = new URLSearchParams();
      
      filteredModes.forEach((mode) => {
        formData.append("modes", mode);
      });
      
      formData.append("tag", tag);
      formData.append("showOnUI", "false");
      formData.append("version", version);
      formData.append("url", normalizedUrl);

      const appToken = await this.getAppToken(appId, partnerToken);

      let response;
      try {
        response = await apiGupShup.post(`/app/${appId}/subscription`, formData, {
          headers: {
            Authorization: appToken, 
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        return {
          status: response.data?.status || "success",
        };
      } catch (postError) {
        if (postError instanceof AxiosError) {
          const errorData = postError.response?.data;
          const errorMessage = errorData?.message || postError.message;
          
          if (errorMessage?.includes("Duplicate component tag") || errorMessage?.includes("Duplicate")) {
            try {
              
              const existingSubscription = await this.getAppSubscription(appId, partnerToken);
              
              if (existingSubscription && existingSubscription.id) {
                const subscriptionId = existingSubscription.id;
                
                const validModesForPutV3 = [
                  "NONE",
                  "MESSAGE",
                  "READ",
                  "DELIVERED",
                  "SENT",
                  "DELETED",
                  "FAILED",
                  "ENQUEUED",
                  "FLOWS_MESSAGE",
                  "PAYMENTS",
                  "TEMPLATE",
                  "ACCOUNT",
                  "BILLING",
                  "ALL",
                  "OTHERS",
                  "COEXISTENCE",
                ];
                const putModes = filteredModes.filter(mode => validModesForPutV3.includes(mode));
                
                if (putModes.length === 0) {
                  putModes.push("ALL");
                }
                
                const updateFormData = new URLSearchParams();
                updateFormData.append("url", normalizedUrl);
                updateFormData.append("tag", tag);
                updateFormData.append("version", version);
                putModes.forEach((mode) => {
                  updateFormData.append("modes", mode);
                });
                
                response = await apiGupShup.put(`/app/${appId}/subscription/${subscriptionId}`, updateFormData, {
                  headers: {
                    Authorization: appToken,
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                });
                
                const responseStatus = response.data?.status || 
                                     (response.data?.subscription ? "success" : null) ||
                                     (response.data?.subscriptions ? "success" : null) ||
                                     "success";
                
                return {
                  status: responseStatus,
                };
              } else {
                throw new Error("Subscription existente não encontrada para atualizar");
              }
            } catch (updateError) {
              throw new Error(
                `Failed to update existing subscription: ${updateError instanceof Error ? updateError.message : "Unknown error"}`
              );
            }
          }
          
          throw postError;
        }
        throw postError;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        
        const errorData = error.response?.data;
        const errorMessage = errorData?.message || error.message;
        
        if (errorMessage?.includes("Invalid URL") || errorMessage?.includes("Invalid URL Passed")) {
          throw new Error(
            `Invalid URL Passed: A URL "${normalizedUrl}" não pôde ser validada pela GupShup. ` +
            `Verifique se: 1) A URL está acessível publicamente via HTTPS, ` +
            `2) A URL responde a requisições HTTP/HTTPS, ` +
            `3) Não há firewall ou restrições bloqueando a validação. ` +
            `Erro original: ${errorMessage}`
          );
        }
        
        throw new Error(
          `Failed to configure subscription: ${errorMessage}`
        );
      }
      throw new Error("Failed to configure subscription");
    }
  }

  async getAppSubscription(appId: string, partnerToken: string): Promise<{
    id?: string;
    active: boolean;
    url?: string;
    tag?: string;
    version?: number;
    modes?: string[];
  } | null> {
    try {
      
      const appToken = await this.getAppToken(appId, partnerToken);

      const response = await apiGupShup.get(`/app/${appId}/subscription`, {
        headers: {
          Authorization: appToken,
        },
      });

      let subscription = response.data?.subscription;
      
      if (!subscription && Array.isArray(response.data?.subscriptions) && response.data.subscriptions.length > 0) {
        
        const subscriptions = response.data.subscriptions;
        subscription = subscriptions.find((sub: any) => sub.tag === "V3 Subscription") || subscriptions[0];
      }

      if (subscription) {
        return {
          id: subscription.id?.toString() || subscription.id,
          active: subscription.active || false,
          url: subscription.url,
          tag: subscription.tag,
          version: subscription.version,
          modes: subscription.modes || [],
        };
      }

      return null;
    } catch (error) {
      if (error instanceof AxiosError) {
        
        if (error.response?.status === 404) {
          return null;
        }
      }
      
      return null;
    }
  }

  async deleteSubscription(
    appId: string,
    subscriptionId: string,
    partnerToken: string
  ): Promise<void> {
    try {
      const appToken = await this.getAppToken(appId, partnerToken);

      await apiGupShup.delete(`/app/${appId}/subscription/${subscriptionId}`, {
        headers: {
          Authorization: appToken,
        },
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("UNAUTHORIZED");
        }
        if (error.response?.status === 404) {
          throw new Error("Subscription not found");
        }
        throw new Error(
          `Failed to delete subscription: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error("Failed to delete subscription");
    }
  }
}

export const gupshupIntegrationService = new GupShupIntegrationService();
